import { useCallback, useRef } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useFlagsService } from '@atlassian/jira-flags';
import type { ErrorResponse } from '@atlassian/jira-servicedesk-common/src/utils/fetch';
import usePortalGroupsResource from '@atlassian/jira-servicedesk-request-types-config-common/src/controllers/portal-groups/index.tsx';
import { NEW_GROUP_ID } from '../../common/constants';
import type { Group } from '../../common/types';
import { REORDER_GROUP } from '../../common/ui/flags/constants.tsx';
import getErrorFlag from '../../common/ui/flags/main.tsx';
import { useActionsProvider } from '../../services/store';
import useGetTicketTypesByGroup from '../get-ticket-types-by-group';
import useToggleState from '../toggle-state';
import useTracking from '../tracking';

type Result = [boolean, boolean, ErrorResponse | undefined, () => void];

const useCreateGroup = (group: Group): Result => {
	const { id } = group;
	const ticketTypes = useGetTicketTypesByGroup(id);
	const { createGroup } = useActionsProvider();
	const [isSaving, toggleSaving] = useToggleState();
	const { fireError, fireTrack } = useTracking();
	const { showFlag } = useFlagsService();
	const errorRef = useRef<ErrorResponse | undefined>();
	const { clear: clearPortalGroupsResourceCache } = usePortalGroupsResource();

	const create = useCallback(() => {
		if (NEW_GROUP_ID === id && !isSaving) {
			errorRef.current = undefined;
			toggleSaving();

			createGroup((result, error) => {
				errorRef.current = error;

				if (result) {
					if (Array.isArray(result)) {
						fireTrack('portalGroups reordered');
					} else {
						fireTrack('portalGroup created', { groupId: result.id });
					}

					clearPortalGroupsResourceCache();
				} else if (error) {
					const reorderFailed = get(error, 'response.reorderFailed', false);

					if (reorderFailed) {
						showFlag(getErrorFlag(REORDER_GROUP, error));
						fireError('reorderGroups', error);
					} else {
						// Only toggle loading state on failure as
						// group component will be removed and replaced on success
						toggleSaving();
						fireError('createGroup', error);
					}
				}
			});
		}
	}, [
		clearPortalGroupsResourceCache,
		createGroup,
		fireError,
		fireTrack,
		id,
		isSaving,
		showFlag,
		toggleSaving,
	]);

	const isValid = Boolean(group.name && !isEmpty(ticketTypes));

	return [isValid, isSaving, errorRef.current, create];
};

export default useCreateGroup;
